import { useState } from 'react';
import { Badge, Button, Popover, List, Empty, Spin } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { notificationsApi } from '@/api/notifications';
import type { NotificationLog } from '@/types';

const POLL_MS = 60_000;

export default function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: countResp } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => (await notificationsApi.getUnreadCount()).data,
    refetchInterval: POLL_MS,
  });
  const unread = countResp?.data?.count ?? 0;

  const { data: feedResp, isLoading } = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: async () => (await notificationsApi.getFeed({ page_size: 20 })).data,
    enabled: open,
  });
  const items: NotificationLog[] = feedResp?.data ?? [];

  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) =>
      notificationsApi.markRead(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'feed'] });
    },
  });

  const handleItemClick = (item: NotificationLog) => {
    if (!item.read_at) markRead.mutate({ ids: [item.id] });
    // Map notification type to the matching IssuesPage tab so the user lands
    // on the relevant list rather than the default Overdue tab.
    const tabByType: Record<string, string> = {
      overdue: 'overdue',
      upcoming: 'upcoming',
      postponed: 'postponed',
      cancelled: 'cancelled',
      ongoing: 'ongoing',
      completed: 'completed',
    };
    const tab = tabByType[item.notification_type] ?? 'overdue';
    setOpen(false);
    navigate(`/issues?tab=${tab}`);
  };

  const content = (
    <div style={{ width: 340, maxHeight: 420, overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>{t('notificationBell.title')}</strong>
        <Button
          type="link"
          size="small"
          disabled={unread === 0 || markRead.isPending}
          onClick={() => markRead.mutate({ all: true })}
        >
          {t('notificationBell.markAllRead')}
        </Button>
      </div>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : items.length === 0 ? (
        <Empty description={t('notificationBell.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              onClick={() => handleItemClick(item)}
              style={{
                cursor: 'pointer',
                background: item.read_at ? 'transparent' : 'rgba(22,119,255,0.08)',
                padding: 8,
                borderRadius: 4,
              }}
            >
              <List.Item.Meta
                title={item.message}
                description={dayjs(item.created_at).format('YYYY-MM-DD HH:mm')}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={unread} size="small" overflowCount={99}>
        <Button
          type="text"
          aria-label={t('notificationBell.title')}
          icon={<BellOutlined />}
          style={{ color: '#999' }}
        />
      </Badge>
    </Popover>
  );
}
